import api from './axiosConfig';

export interface RegisterFaceRequest {
  employeeId: string;
  faceImageBase64: string;
  profileName?: string;
  setAsPrimary?: boolean;
}

export interface VerifyFaceRequest {
  faceImageBase64: string;
  employeeIdHint?: string;
  matchThreshold?: number;
}

export interface VerifyFaceResult {
  isVerified: boolean;
  employeeId?: string;
  employeeName?: string;
  employeeCode?: string;
  confidence?: number;
  errorMessage?: string;
}

export interface FaceProfileDto {
  id: string;
  profileName: string;
  faceImageBase64: string;
  isPrimary: boolean;
  isActive: boolean;
  lastUsedAt?: string;
  successfulPunches: number;
  failedMatchAttempts: number;
  createdAt: string;
  createdBy?: string;
}

class FaceApi {
  /**
   * Register a face profile for an employee
   */
  async registerFace(request: RegisterFaceRequest): Promise<string> {
    try {
      const response = await api.post('/face/register', request);

      return response.data.profileId;
    } catch (error) {
      console.error('Face registration failed:', error);
      throw error;
    }
  }

  /**
   * Verify a face image and identify employee
   * Can be used anonymously for kiosk mode
   */
  async verifyFace(request: VerifyFaceRequest): Promise<VerifyFaceResult> {
    try {
      const response = await api.post('/face/verify', request);

      return response.data;
    } catch (error) {
      console.error('Face verification failed:', error);
      throw error;
    }
  }

  /**
   * Get all face profiles for an employee
   */
  async getEmployeeFaceProfiles(employeeId: string): Promise<FaceProfileDto[]> {
    try {
      const response = await api.get(`/face/employee/${employeeId}`);

      return response.data;
    } catch (error) {
      console.error('Failed to get face profiles:', error);
      throw error;
    }
  }

  /**
   * Delete a specific face profile
   */
  async deleteFaceProfile(profileId: string): Promise<void> {
    try {
      await api.delete(`/face/${profileId}`);
    } catch (error) {
      console.error('Failed to delete face profile:', error);
      throw error;
    }
  }

  /**
   * Delete all face profiles for an employee
   */
  async deleteAllEmployeeFaceProfiles(employeeId: string): Promise<void> {
    try {
      await api.delete(`/face/employee/${employeeId}/all`);
    } catch (error) {
      console.error('Failed to delete all face profiles:', error);
      throw error;
    }
  }

  /**
   * Enable or disable face attendance for one employee
   */
  async setEmployeeFaceAttendance(employeeId: string, enabled: boolean): Promise<void> {
    try {
      await api.put(`/face/employee/${employeeId}/attendance-enabled`, { enabled });
    } catch (error) {
      console.error('Failed to update employee face attendance setting:', error);
      throw error;
    }
  }
}

export const faceApi = new FaceApi();
